'use client';

import { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

interface SessionIdentity {
  email: string;
  role: string | null;
  loading: boolean;
}

interface SessionIdentityControlsProps {
  /** 'commercial' | 'client' — determines logout redirect target */
  space: 'commercial' | 'client';
  /** compact = inline badge (desktop), full = dropdown (mobile) */
  variant?: 'compact' | 'full';
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'OWNER',
  admin: 'ADMIN',
  manager: 'MANAGER',
  commercial: 'COMMERCIAL',
  production: 'PRODUCTION',
  client: 'CLIENT',
};

const ROLE_COLORS: Record<string, string> = {
  owner: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  admin: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  manager: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
  commercial: 'bg-[#00d4c8]/20 text-[#00d4c8] border-[#00d4c8]/30',
  production: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  client: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
};

export default function SessionIdentityControls({
  space,
  variant = 'compact',
}: SessionIdentityControlsProps) {
  const router = useRouter();
  const [identity, setIdentity] = useState<SessionIdentity>({
    email: '',
    role: null,
    loading: true,
  });
  const [signingOut, setSigningOut] = useState(false);

  const loginRoute = space === 'commercial' ? '/commercial/login' : '/client/login';

  const resolveIdentity = useCallback(async () => {
    const supabase = createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      setIdentity({ email: '', role: null, loading: false });
      return;
    }

    // Resolve role from organization_members — NOT from email
    const { data: memberData } = await supabase
      .from('organization_members')
      .select('role')
      .eq('user_id', user.id)
      .eq('status', 'active')
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();

    setIdentity({
      email: user.email ?? '',
      role: memberData?.role ?? null,
      loading: false,
    });
  }, []);

  useEffect(() => {
    resolveIdentity();

    const supabase = createClient();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setIdentity({ email: '', role: null, loading: false });
      } else {
        resolveIdentity();
      }
    });

    return () => subscription.unsubscribe();
  }, [resolveIdentity]);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      const supabase = createClient();
      await supabase.auth.signOut();
      router.replace(loginRoute);
      router.refresh();
    } catch {
      setSigningOut(false);
    }
  };

  const handleSwitchAccount = async () => {
    setSigningOut(true);
    try {
      const supabase = createClient();
      await supabase.auth.signOut();
      router.replace(loginRoute);
      router.refresh();
    } catch {
      setSigningOut(false);
    }
  };

  if (identity.loading) {
    return (
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 border border-white/20 border-t-white/60 rounded-full animate-spin" />
      </div>
    );
  }

  if (!identity.email) {
    return (
      <a
        href={loginRoute}
        className="text-xs text-gray-400 hover:text-[#00d4c8] transition-colors border border-white/10 rounded px-3 py-1.5"
      >
        Se connecter
      </a>
    );
  }

  const roleLabel = identity.role ? (ROLE_LABELS[identity.role] ?? identity.role.toUpperCase()) : '—';
  const roleColor = identity.role ? (ROLE_COLORS[identity.role] ?? 'bg-gray-500/20 text-gray-300 border-gray-500/30') : 'bg-gray-500/20 text-gray-300 border-gray-500/30';

  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-3">
        {/* Identity block — always visible on desktop */}
        <div className="hidden sm:flex flex-col items-end gap-0.5">
          <span className="text-xs text-white/80 font-mono leading-none">{identity.email}</span>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border leading-none ${roleColor}`}>
            {roleLabel}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleSwitchAccount}
            disabled={signingOut}
            title="Changer de compte"
            className="hidden sm:flex items-center gap-1 text-xs text-gray-400 hover:text-white border border-white/10 hover:border-white/20 rounded px-2 py-1.5 transition-all disabled:opacity-50"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Changer
          </button>

          <button
            onClick={handleSignOut}
            disabled={signingOut}
            title="Se déconnecter"
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-400 border border-white/10 hover:border-red-500/30 rounded px-2 py-1.5 transition-all disabled:opacity-50"
          >
            {signingOut ? (
              <span className="w-3 h-3 border border-current/30 border-t-current rounded-full animate-spin" />
            ) : (
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            )}
            <span className="hidden sm:inline">Déconnexion</span>
          </button>
        </div>

        {/* Mobile: show email + role badge + logout only */}
        <div className="flex sm:hidden items-center gap-2">
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border leading-none ${roleColor}`}>
            {roleLabel}
          </span>
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            className="text-xs text-gray-400 hover:text-red-400 transition-colors"
          >
            {signingOut ? '…' : '↩'}
          </button>
        </div>
      </div>
    );
  }

  // full variant
  return (
    <div className="flex flex-col gap-2 p-3 bg-white/5 border border-white/10 rounded-lg">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-xs text-white/80 font-mono truncate">{identity.email}</span>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border leading-none self-start ${roleColor}`}>
            {roleLabel}
          </span>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={handleSwitchAccount}
          disabled={signingOut}
          className="flex-1 text-xs text-gray-400 hover:text-white border border-white/10 hover:border-white/20 rounded px-2 py-1.5 transition-all disabled:opacity-50"
        >
          Changer de compte
        </button>
        <button
          onClick={handleSignOut}
          disabled={signingOut}
          className="flex-1 text-xs text-gray-400 hover:text-red-400 border border-white/10 hover:border-red-500/30 rounded px-2 py-1.5 transition-all disabled:opacity-50"
        >
          {signingOut ? 'Déconnexion…' : 'Se déconnecter'}
        </button>
      </div>
    </div>
  );
}
